import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Instagram, Facebook, Youtube } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/lib/data";
import { Logo, scrollToId } from "@/components/Logo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4"
        data-testid="site-navbar"
      >
        <nav
          className={`mt-4 w-full max-w-6xl flex items-center justify-between rounded-full px-5 sm:px-7 py-3 transition-all duration-500 ${
            scrolled
              ? "bg-[var(--bg)]/80 backdrop-blur-xl border border-[var(--border-gold)] shadow-[0_8px_40px_-24px_rgba(35,31,32,0.4)]"
              : "bg-transparent border border-transparent"
          }`}
        >
          <button
            onClick={() => go("home")}
            className="flex items-center group"
            data-testid="nav-logo-button"
          >
            <Logo className="h-11 sm:h-14 w-auto transition-transform duration-500 group-hover:scale-105" />
          </button>

          <div className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="link-underline text-[13px] tracking-wide text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors"
                data-testid={`nav-link-${l.href}`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-1 mr-1">
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="p-2 text-[var(--ink-2)] hover:text-[var(--gold)] transition-colors" data-testid="nav-instagram">
                <Instagram size={17} />
              </a>
              <a href={CONTACT.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="p-2 text-[var(--ink-2)] hover:text-[var(--gold)] transition-colors" data-testid="nav-facebook">
                <Facebook size={17} />
              </a>
              <a href={CONTACT.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="p-2 text-[var(--ink-2)] hover:text-[var(--gold)] transition-colors" data-testid="nav-youtube">
                <Youtube size={17} />
              </a>
            </div>
            <button
              onClick={() => go("contact")}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[var(--ink)] text-[var(--bg)] text-[12px] tracking-wide px-5 py-2.5 hover:bg-[var(--gold)] transition-colors duration-400"
              data-testid="nav-cta-button"
            >
              Plan Your Wedding
            </button>
            <button
              className="lg:hidden p-2 text-[var(--ink)]"
              onClick={() => setOpen((o) => !o)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden bg-[var(--bg)]/97 backdrop-blur-xl flex flex-col items-center justify-center gap-6"
            data-testid="mobile-menu"
          >
            {NAV_LINKS.map((l, i) => (
              <motion.button
                key={l.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => go(l.href)}
                className="font-serif text-3xl text-[var(--ink)]"
                data-testid={`mobile-nav-link-${l.href}`}
              >
                {l.label}
              </motion.button>
            ))}
            <a
              href={`tel:${CONTACT.phone}`}
              className="mt-4 flex items-center gap-2 text-[var(--gold)]"
              data-testid="mobile-phone-link"
            >
              <Phone size={16} /> {CONTACT.phoneDisplay}
            </a>
            <div className="flex items-center gap-5 mt-2">
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-[var(--ink-2)] hover:text-[var(--gold)]" data-testid="mobile-instagram"><Instagram size={22} /></a>
              <a href={CONTACT.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-[var(--ink-2)] hover:text-[var(--gold)]" data-testid="mobile-facebook"><Facebook size={22} /></a>
              <a href={CONTACT.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="text-[var(--ink-2)] hover:text-[var(--gold)]" data-testid="mobile-youtube"><Youtube size={22} /></a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
